import { useState, useEffect } from "react";

interface TutorialStep {
  id: number;
  title: string;
  description: string;
  icon: string;
  dept: string;
  deptColor: string;
  tip: string;
}

const steps: TutorialStep[] = [
  {
    id: 1,
    title: "대시보드 확인",
    description: "출근하면 먼저 대시보드를 확인하세요. 가상 자본금 잔고, 명성 포인트, 진행 중인 프로젝트 달성률을 한눈에 파악할 수 있습니다. 오늘의 할 일과 긴급 알림 팝업도 확인하는 습관을 들이세요.",
    icon: "ri-dashboard-3-line",
    dept: "대시보드",
    deptColor: "text-[#E8375F]",
    tip: "랜덤 이벤트 팝업이 뜨면 즉각 대응해야 명성 포인트 손실을 막을 수 있어요!",
  },
  {
    id: 2,
    title: "인박스 / 메신저 확인",
    description: "AI NPC 거래처들이 메일을 보내옵니다. 유통사 김과장, 디자이너 박윤아, 광고주 이상민 등과 실제처럼 협상하고 답장하세요. 메일 응대 퀄리티에 따라 계약 조건이 달라집니다.",
    icon: "ri-mail-line",
    dept: "인박스",
    deptColor: "text-[#FF9F43]",
    tip: "AI 거래처마다 성격이 달라요. 데이터 중시형, 예술가형, 갑질형... 상대를 파악하고 맞춤 대응하세요!",
  },
  {
    id: 3,
    title: "A&R 크리에이티브 허브",
    description: "앨범 콘셉트를 잡고 데모곡을 수급하는 곳입니다. 무드보드로 비주얼 방향을 설정하고, 가상 작곡가에게 데모를 발주하세요. AI 총괄 프로듀서에게 기획서를 제출하면 75점 이상이어야 통과됩니다.",
    icon: "ri-music-2-line",
    dept: "A&R 허브",
    deptColor: "text-[#C97FFF]",
    tip: "기획서 점수가 낮으면 프로듀서가 거절합니다. 타깃 층, 콘셉트, 차별점을 구체적으로 작성하세요!",
  },
  {
    id: 4,
    title: "전자결재 시스템",
    description: "업무 진행에 필요한 모든 계약서와 예산안은 전자결재를 거쳐야 합니다. 양식에 맞게 작성 후 '결재 올리기'를 누르면 AI가 조건을 검토합니다. 조건이 맞지 않으면 반려되니 꼼꼼히 확인하세요.",
    icon: "ri-file-list-3-line",
    dept: "전자결재",
    deptColor: "text-[#00C896]",
    tip: "계약서 독소조항은 법무 페이지에서 먼저 검토한 뒤 결재 올리세요. 불리한 조항이 숨어 있을 수 있어요!",
  },
  {
    id: 5,
    title: "재무 / 회계 ERP",
    description: "자본금 관리와 예산 집행을 담당합니다. 가상 은행 계좌에서 입출금을 확인하고, 부서별 예산 소진율을 모니터링하세요. 세금 납부 기한을 놓치면 신용도가 깎입니다!",
    icon: "ri-bank-line",
    dept: "재무 ERP",
    deptColor: "text-[#00C896]",
    tip: "마케팅 부서에서 예산 초과 결재가 올라오면 재무 담당자가 승인 또는 삭감 결정을 내려야 해요.",
  },
  {
    id: 6,
    title: "마케팅 워룸",
    description: "완성된 앨범을 홍보하고 여론을 관리합니다. SNS 포스팅 텍스트를 작성하면 AI가 예상 반응을 알려줍니다. 갑자기 터지는 위기(논란)에는 즉시 해명 보도자료를 배포해야 합니다.",
    icon: "ri-megaphone-line",
    dept: "마케팅 워룸",
    deptColor: "text-[#FF9F43]",
    tip: "보도자료 퀄리티가 높을수록 언론 반응도 좋아집니다. 핵심 메시지와 공감 포인트를 담아 작성하세요!",
  },
  {
    id: 7,
    title: "법무 & IP 관리",
    description: "외부에서 들어온 계약서의 독소조항을 AI가 자동으로 탐지합니다. 위험 조항을 발견하면 즉시 수정 요청 메일을 발송하세요. 저작권 등록도 잊지 말고 챙겨야 음원 수익이 제대로 들어옵니다.",
    icon: "ri-scales-3-line",
    dept: "법무 IP",
    deptColor: "text-[#FF9F43]",
    tip: "수익의 80%를 가져가는 독소조항 같은 함정이 있어요. AI 탐지를 무조건 먼저 돌리고 서명하세요!",
  },
];

interface Props {
  onClose: () => void;
}

export default function OnboardingTutorial({ onClose }: Props) {
  const [currentStep, setCurrentStep] = useState(0);
  const [closing, setClosing] = useState(false);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 300);
    localStorage.setItem("k-artpen-tutorial-done", "1");
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${closing ? "opacity-0" : "opacity-100"}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-[#111318] border border-[#2A2D3E] rounded-2xl w-full max-w-xl mx-4 overflow-hidden shadow-2xl">
        {/* Top bar */}
        <div className="px-6 pt-6 pb-4 border-b border-[#252830]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <img
                src="https://storage.readdy-site.link/project_files/e81f0f74-7f28-46c8-8485-3df778381a50/9a17726c-ea2b-4ef1-b154-44e1a8dd8821_Gemini_Generated_Image_at6qf4at6qf4at6q-removebg-preview.png?v=7e9fc5a9989fd2f2c3e90b758ae037ba"
                alt="K-ArtPEN"
                className="w-7 h-7 object-contain rounded-md"
              />
              <span className="text-white font-bold text-sm">K-ArtPEN 온보딩 가이드</span>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="w-7 h-7 flex items-center justify-center text-[#5A5E72] hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-[#252830]"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-1.5 mb-3">
            {steps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setCurrentStep(i)}
                className={`flex-1 h-1 rounded-full transition-all cursor-pointer ${
                  i < currentStep ? "bg-[#E8375F]" : i === currentStep ? "bg-[#E8375F]" : "bg-[#252830]"
                }`}
              />
            ))}
          </div>
          <div className="text-[#5A5E72] text-xs">
            {currentStep + 1} / {steps.length} 단계
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-[#1C1F2E] border border-[#2A2D3E] shrink-0`}>
              <i className={`${step.icon} text-2xl ${step.deptColor}`}></i>
            </div>
            <div>
              <div className={`text-xs font-bold mb-1 ${step.deptColor}`}>{step.dept}</div>
              <h3 className="text-white font-bold text-lg leading-tight">{step.title}</h3>
            </div>
          </div>

          <p className="text-[#8B91A8] text-sm leading-relaxed mb-5">{step.description}</p>

          {/* Tip */}
          <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-4 flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-[#FF9F43] text-base"></i>
            </div>
            <div className="text-[#8B91A8] text-xs leading-relaxed">
              <span className="text-[#FF9F43] font-semibold">Pro Tip: </span>
              {step.tip}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
              currentStep === 0
                ? "text-[#3E4155] cursor-not-allowed"
                : "text-[#8B91A8] hover:text-white cursor-pointer hover:bg-[#1C1F2E]"
            }`}
          >
            <i className="ri-arrow-left-s-line"></i>이전
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-[#5A5E72] text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              건너뛰기
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#E8375F] to-[#FF4B7A] text-white text-sm font-bold rounded-xl cursor-pointer hover:shadow-lg hover:shadow-[#E8375F]/25 transition-all whitespace-nowrap"
            >
              {currentStep < steps.length - 1 ? (
                <>다음<i className="ri-arrow-right-s-line"></i></>
              ) : (
                <>시작하기! <i className="ri-rocket-line"></i></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function useOnboardingTutorial() {
  const [show, setShow] = useState(() => {
    return !localStorage.getItem("k-artpen-tutorial-done");
  });
  return { show, setShow };
}
